export type HotkeyAction = 'spin' | 'auto' | 'turbo';

export interface HotkeyBindings {
  readonly version: 1;
  readonly spin: string;
  readonly auto: string;
  readonly turbo: string;
}

export const DEFAULT_HOTKEYS: HotkeyBindings = Object.freeze({
  version: 1,
  spin: 'Space',
  auto: 'KeyN',
  turbo: 'KeyM',
});

export interface HotkeyContext {
  readonly dialogOpen: boolean;
  readonly spinning: boolean;
  readonly autoActive: boolean;
  readonly canSpin: boolean;
  readonly canToggleAuto: boolean;
  readonly canToggleTurbo: boolean;
}

export interface HotkeyActions {
  readonly spin: () => void;
  readonly auto: () => void;
  readonly turbo: () => void;
}

export function isTextEditingTarget(target: EventTarget | null): boolean {
  if (target === null || typeof target !== 'object') return false;
  const candidate = target as { tagName?: unknown; isContentEditable?: unknown };
  const tag = typeof candidate.tagName === 'string' ? candidate.tagName.toUpperCase() : '';
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || candidate.isContentEditable === true;
}

export function isAllowedHotkeyCode(code: string): boolean {
  return code === 'Space'
    || /^Key[A-Z]$/.test(code)
    || /^Digit[0-9]$/.test(code)
    || /^Numpad[0-9]$/.test(code);
}

export function decodeHotkeys(value: unknown): HotkeyBindings {
  if (typeof value !== 'object' || value === null) return DEFAULT_HOTKEYS;
  const candidate = value as Partial<Record<keyof HotkeyBindings, unknown>>;
  const codes = [candidate.spin, candidate.auto, candidate.turbo];
  if (candidate.version !== 1 || !codes.every((code) => typeof code === 'string' && isAllowedHotkeyCode(code))) {
    return DEFAULT_HOTKEYS;
  }
  if (new Set(codes).size !== codes.length) return DEFAULT_HOTKEYS;
  return { version: 1, spin: candidate.spin as string, auto: candidate.auto as string, turbo: candidate.turbo as string };
}

export function getHotkeyLabel(code: string): string {
  if (code === 'Space') return 'Leertaste';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  return code;
}

export class HotkeyController {
  private bindings: HotkeyBindings;

  constructor(
    bindings: HotkeyBindings,
    private readonly getContext: () => HotkeyContext,
    private readonly actions: HotkeyActions,
  ) {
    this.bindings = decodeHotkeys(bindings);
  }

  bind(target: Window = window): () => void {
    target.addEventListener('keydown', this.handleKeydown);
    return () => target.removeEventListener('keydown', this.handleKeydown);
  }

  setBindings(bindings: HotkeyBindings): void {
    this.bindings = decodeHotkeys(bindings);
  }

  getBindings(): HotkeyBindings { return this.bindings; }

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (event.repeat || event.ctrlKey || event.metaKey || event.altKey || isTextEditingTarget(event.target)) return;
    const context = this.getContext();
    if (context.dialogOpen) return;
    if (event.code === this.bindings.spin) {
      // Space is also the browser's native activation key for a focused
      // button. Consume it while Auto is active so it can never click the
      // previously focused Auto control as an unintended stop request.
      if (context.autoActive) {
        event.preventDefault();
        return;
      }
      if (!context.canSpin) return;
      event.preventDefault();
      this.actions.spin();
      return;
    }
    if (event.code === this.bindings.auto) {
      if (!context.canToggleAuto) return;
      event.preventDefault();
      this.actions.auto();
      return;
    }
    if (event.code === this.bindings.turbo) {
      if (!context.canToggleTurbo || context.spinning) return;
      event.preventDefault();
      this.actions.turbo();
    }
  };
}
